import Vue from 'vue';
import ResizeObserverPolyfill from '@juggle/resize-observer';
import { Turbo } from '@hotwired/turbo-rails';

/**
 * We need to disable Turbo navigation on the whole page by default since
 * it would break most of our current implementations by intercepting
 * all links and form submissions.
 *
 * In order to make this "progressive" we're gonna have to use `data-turbo="true"`
 * on every element that we want to use Turbo with.
 * https://turbo.hotwired.dev/handbook/drive#disabling-turbo-drive-on-specific-links-or-forms
 */
Turbo.session.drive = false;

// <turbo-stream action="reload" target="frame-id"></turbo-stream>
Turbo.StreamActions.reload = function () {
  document.getElementById(this.getAttribute('target'))?.reload();
};

// <turbo-stream action="reset_alpine_store" target="store-name"></turbo-stream>
Turbo.StreamActions.reset_alpine_store = function () {
  window.Alpine?.store(this.target)?.reset?.();
};

// Following the naming conventions
// https://github.com/marcoroth/turbo_power-rails#browser-history-actions
//
// <turbo-stream action="replace_state" url="url"></turbo-stream>
window.addEventListener('turbo:load', () => {
  document.addEventListener('turbo:before-stream-render', (event) => {
    if (event.target.action === 'replace_state') {
      const url = event.target.attributes.url.value;

      history.replaceState(null, null, url);
      event.preventDefault();
    }
  });
});

// Usually instead of the url to perform the "turbo request" this would be the message.
// But since we might want to have different content or slighly different behavior depending
// on the scenario that this component is used in, we made this flexible choice the default.
Turbo.config.forms.confirm = (urlToLoadTheConfirmModalFrame) => {
  Turbo.visit(urlToLoadTheConfirmModalFrame, { frame: 'confirm-modal' });

  // Turbo waits for the promise to resolve, so we can attach a "once" listener to the window here
  // that will return the result of the user's interaction.
  return new Promise((resolve) => {
    // `event.detail` will be either `true` or `false`
    window.addEventListener('zone:confirmed', (event) => resolve(event.detail), { once: true });
  });
};

// Useful when a redirect is needed after a turbo request and we are inside a <turbo-frame> (e.g. modal)
// Not using `Turbo.visit` because it was preventing flash messages to work properly.
// It can be delayed by passing a <delay> tag as content (e.g. turbo_stream.action(:redirect, url, '<delay>3000</delay>')
// <turbo-stream action="redirect" target="url"></turbo-stream>
Turbo.StreamActions.redirect = function () {
  const delayTag = this.querySelector('template')?.content?.querySelector('delay');
  const delay = parseInt(delayTag?.textContent || '0', 10);

  setTimeout(() => {
    window.location.href = this.target;
  }, delay);
};

// This can be used to refresh the current page after a delay (in milliseconds).
// Useful when you want to show a flash message and then refresh the page after a few seconds.
// <turbo-stream action="delayed_refresh" target="delay_in_miliseconds"></turbo-stream>
Turbo.StreamActions.delayed_refresh = function () {
  const delay = parseInt(this.target || '3000', 10);

  setTimeout(() => {
    window.location.reload();
  }, delay);
};

// This monkey patch it's in order to support Turbo's morphing functionality without breaking Alpine's
// event shorthand attribute syntax. Not doing this would error everytime Turbo's morphing library
// `idiomorph` tries to update/modify an attribute like `@click` which is standard with Alpine.
let setAttributePatched = false;

function monkeyPatchDomSetAttributeToAllowAtSymbols() {
  if (setAttributePatched) {
    return;
  }

  setAttributePatched = true;

  // Because idiomorph may add attributes to elements containing "@" symbols
  // like in the case of an Alpine `@click` directive, we have to patch
  // the standard Element.setAttribute method to allow this to work.
  const original = Element.prototype.setAttribute;
  const hostDiv = document.createElement('div');

  Element.prototype.setAttribute = function newSetAttribute(name, value) {
    if (!name.includes('@')) {
      return original.call(this, name, value);
    }

    // eslint-disable-next-line no-unsanitized/property
    hostDiv.innerHTML = `<span ${name}="${value}"></span>`;

    const attr = hostDiv.firstElementChild.getAttributeNode(name);

    hostDiv.firstElementChild.removeAttributeNode(attr);

    this.setAttributeNode(attr);
  };
}

monkeyPatchDomSetAttributeToAllowAtSymbols();

window.ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;

if (import.meta.env.VITE_PROFILE_VUE === 'true') {
  Vue.config.devtools = true;
  Vue.config.performance = true;
}
